// AudioEngine: owns the AudioContext, builds the mixer graph from project state,
// runs the transport (play/stop/record position), schedules clip playback,
// the metronome, and exposes metering. UI and recorder talk to this.
import { Store } from "../state/store";
import { Track, clipSpeed } from "../state/model";
import { audioPlans, AudioPlan } from "../state/transitions";

/** Gain envelope for one scheduled clip: fade in/out ramps (from the plan, so
 *  crossfades are included), correct even when playback starts mid-fade.
 *  `from` = the timeline time playback of this clip begins; `at` = its ctx time. */
export function scheduleFades(p: AudioParam, gain: number, plan: AudioPlan, from: number, at: number) {
  const toCtx = (t: number) => at + (t - from);
  const end = plan.end, fiEnd = plan.start + plan.fadeIn, foStart = end - plan.fadeOut;
  const level = (t: number) => {
    let g = gain;
    if (plan.fadeIn > 0 && t < fiEnd) g = Math.min(g, gain * Math.max(0, (t - plan.start) / plan.fadeIn));
    if (plan.fadeOut > 0 && t > foStart) g = Math.min(g, gain * Math.max(0, (end - t) / plan.fadeOut));
    return g;
  };
  p.setValueAtTime(level(from), at);
  const fiTo = Math.min(fiEnd, foStart);
  if (plan.fadeIn > 0 && fiTo > from) p.linearRampToValueAtTime(level(fiTo), toCtx(fiTo));
  if (plan.fadeOut > 0) {
    if (foStart > from) p.setValueAtTime(level(foStart), toCtx(foStart));
    p.linearRampToValueAtTime(0, toCtx(end));
  }
}
import { createEffectNode, EffectNode } from "./effects";

interface Channel {
  fxInput: GainNode; // clips/inputs connect here
  panner: StereoPannerNode;
  fader: GainNode;
  analyser: AnalyserNode;
  fxNodes: FxSlot[];
}

/** A live effect node plus the params it last applied (so unchanged effects
 *  aren't re-pushed on every store change). */
interface FxSlot { id: string; node: EffectNode; applied: string; }

export class AudioEngine {
  ctx: AudioContext;
  private store: Store;
  private channels = new Map<string, Channel>();
  private masterFxInput!: GainNode;
  private masterFader!: GainNode;
  private masterAnalyser!: AnalyserNode;
  private masterFxNodes: FxSlot[] = [];

  /** Externally-managed insert nodes (e.g. WAM plugins), keyed by trackId or "master". */
  externalInserts = new Map<string, AudioNode[]>();

  private sources = new Set<AudioBufferSourceNode>();
  playing = false;
  recording = false;
  private startCtxTime = 0;
  private startPos = 0;
  private pausedPos = 0;
  private loopTimer: any = null;

  metronome = false;
  private metroTimer: any = null;
  private metroNextBeatTime = 0;
  private metroBeat = 0;

  onTransport?: () => void; // fired on play/stop/record state changes

  private meterBuf = new Float32Array(1024);

  constructor(store: Store) {
    this.store = store;
    this.ctx = new AudioContext({ latencyHint: "interactive" });
    this.buildMaster();
    store.on("change", ({ scope }) => {
      if (scope === "tracks" || scope === "mixer" || scope === "project") this.syncGraph();
      // Clip edits during playback: re-schedule so you hear the new arrangement.
      if ((scope === "tracks" || scope === "project") && this.playing) this.queueReschedule();
    });
    this.syncGraph();
  }

  async resume() {
    if (this.ctx.state !== "running") await this.ctx.resume();
  }

  /** Route output to a specific device (Chrome 110+). No-op where unsupported. */
  async setSinkId(deviceId: string) {
    const anyCtx = this.ctx as any;
    if (typeof anyCtx.setSinkId === "function") {
      try { await anyCtx.setSinkId(deviceId); } catch (e) { console.warn("setSinkId", e); }
    }
  }

  // ---- graph construction ---------------------------------------------
  private buildMaster() {
    this.masterFxInput = this.ctx.createGain();
    this.masterFader = this.ctx.createGain();
    this.masterAnalyser = this.ctx.createAnalyser();
    this.masterAnalyser.fftSize = 2048;
    this.masterFader.connect(this.masterAnalyser);
    this.masterAnalyser.connect(this.ctx.destination);
    this.rebuildMasterFx();
  }

  private masterSig = "";
  /** Rebuild the master insert chain. Only when its structure changed (or `force`)
   *  — re-creating nodes on every edit glitches audio mid-playback. */
  private rebuildMasterFx(force = false) {
    const fxList = this.store.project.masterFx.filter((f) => f.kind !== "wam" && f.enabled);
    const sig = fxList.map((f) => f.id).join(",") + "|" + (this.externalInserts.get("master")?.length ?? 0);
    this.glide(this.masterFader.gain, this.store.project.masterVolume);
    if (!force && sig === this.masterSig) { this.updateFx(this.masterFxNodes, fxList); return; }
    this.masterSig = sig;
    this.masterFxNodes.forEach((f) => f.node.dispose());
    this.masterFxNodes = [];
    try { this.masterFxInput.disconnect(); } catch {}
    let cursor: AudioNode = this.masterFxInput;
    for (const fx of fxList) {
      const node = createEffectNode(this.ctx, fx);
      cursor.connect(node.input);
      cursor = node.output;
      this.masterFxNodes.push({ id: fx.id, node, applied: JSON.stringify(fx.params) });
    }
    for (const node of this.externalInserts.get("master") ?? []) { cursor.connect(node); cursor = node; }
    cursor.connect(this.masterFader);
  }

  /** Push changed params into live effect nodes (skips unchanged ones). */
  private updateFx(slots: FxSlot[], fxList: { id: string; params: any }[]) {
    for (const fx of fxList) {
      const slot = slots.find((f) => f.id === fx.id);
      if (!slot) continue;
      const sig = JSON.stringify(fx.params);
      if (slot.applied !== sig) { slot.node.update(fx.params); slot.applied = sig; }
    }
  }

  private primed = new WeakSet<AudioParam>();
  /** Set a mixer param: exact the first time, then a short glide (no zipper clicks). */
  private glide(p: AudioParam, v: number) {
    if (!this.primed.has(p)) { this.primed.add(p); p.value = v; return; }
    if (Math.abs(p.value - v) < 1e-6) return;
    p.cancelScheduledValues(this.ctx.currentTime);
    p.setTargetAtTime(v, this.ctx.currentTime, 0.015);
  }

  private buildChannel(track: Track): Channel {
    const fxInput = this.ctx.createGain();
    const panner = this.ctx.createStereoPanner();
    const fader = this.ctx.createGain();
    const analyser = this.ctx.createAnalyser();
    analyser.fftSize = 1024;
    const ch: Channel = { fxInput, panner, fader, analyser, fxNodes: [] };
    this.wireChannelFx(track, ch);
    return ch;
  }

  private wireChannelFx(track: Track, ch: Channel) {
    ch.fxNodes.forEach((f) => f.node.dispose());
    ch.fxNodes = [];
    try { ch.fxInput.disconnect(); } catch {}
    let cursor: AudioNode = ch.fxInput;
    for (const fx of track.fx) {
      if (fx.kind === "wam" || !fx.enabled) continue;
      const node = createEffectNode(this.ctx, fx);
      cursor.connect(node.input);
      cursor = node.output;
      ch.fxNodes.push({ id: fx.id, node, applied: JSON.stringify(fx.params) });
    }
    for (const node of this.externalInserts.get(track.id) ?? []) { cursor.connect(node); cursor = node; }
    cursor.connect(ch.panner);
    ch.panner.connect(ch.fader);
    ch.fader.connect(ch.analyser);
    ch.analyser.connect(this.masterFxInput);
  }

  /** Reconcile channels with current tracks; update live params. Video tracks are
   *  purely visual (their audio lives on separate audio lanes) — no mixer channel. */
  syncGraph() {
    const audioTracks = this.store.project.tracks.filter((t) => t.kind !== "video");
    const ids = new Set(audioTracks.map((t) => t.id));
    // remove channels for deleted (or now-video) tracks
    for (const [id, ch] of this.channels) {
      if (!ids.has(id)) {
        ch.fxNodes.forEach((f) => f.node.dispose());
        [ch.fxInput, ch.panner, ch.fader, ch.analyser].forEach((n) => { try { n.disconnect(); } catch {} });
        this.channels.delete(id);
      }
    }
    const anySolo = this.store.anySolo();
    for (const track of audioTracks) {
      let ch = this.channels.get(track.id);
      if (!ch) { ch = this.buildChannel(track); this.channels.set(track.id, ch); }
      // rebuild fx chain if structure changed
      const liveIds = ch.fxNodes.map((f) => f.id).join(",");
      const wantIds = track.fx.filter((f) => f.enabled && f.kind !== "wam").map((f) => f.id).join(",");
      if (liveIds !== wantIds) this.wireChannelFx(track, ch);
      else this.updateFx(ch.fxNodes, track.fx);
      // live params (glided, so fader/pan moves don't click)
      const audible = !track.mute && (!anySolo || track.solo);
      this.glide(ch.fader.gain, audible ? track.volume : 0);
      this.glide(ch.panner.pan, track.pan);
    }
    this.rebuildMasterFx();
  }

  channelInput(trackId: string): GainNode | undefined {
    return this.channels.get(trackId)?.fxInput;
  }

  addExternalInsert(target: string, node: AudioNode) {
    const list = this.externalInserts.get(target) ?? [];
    list.push(node);
    this.externalInserts.set(target, list);
    this.rewire(target);
  }
  removeExternalInsert(target: string, node: AudioNode) {
    const list = this.externalInserts.get(target);
    if (!list) return;
    const i = list.indexOf(node);
    if (i >= 0) list.splice(i, 1);
    try { node.disconnect(); } catch {}
    this.rewire(target);
  }
  private rewire(target: string) {
    if (target === "master") { this.rebuildMasterFx(true); return; }
    const track = this.store.track(target);
    const ch = this.channels.get(target);
    if (track && ch) this.wireChannelFx(track, ch);
  }

  // ---- transport -------------------------------------------------------
  /** A loop pass that has been scheduled but not reached yet (sample-accurate looping). */
  private nextPass: { at: number; pos: number } | null = null;

  /** While playing: the audio-clock time `at` when the timeline is at `pos`. */
  anchor(): { pos: number; at: number } | null {
    if (!this.playing) return null;
    this.position(); // folds in a pending loop pass
    return { pos: this.startPos, at: this.startCtxTime };
  }

  position(): number {
    if (!this.playing) return this.pausedPos;
    const t = this.ctx.currentTime;
    if (this.nextPass && t >= this.nextPass.at) {
      this.startPos = this.nextPass.pos; this.startCtxTime = this.nextPass.at; this.nextPass = null;
    }
    return this.startPos + Math.max(0, t - this.startCtxTime);
  }

  /** Start playback from `from`. With `at` (an audio-clock time), the timeline is
   *  at `from` exactly at `at` — used to start in step with a remote partner; if
   *  `at` has already passed, playback joins further along so it stays aligned. */
  async play(from?: number, at?: number) {
    await this.resume();
    if (this.playing) this.stopSources();
    clearTimeout(this.loopTimer); this.nextPass = null;
    let pos = from ?? this.pausedPos;
    let when = this.ctx.currentTime + 0.06;
    if (at != null && isFinite(at)) {
      const earliest = this.ctx.currentTime + 0.02;
      if (at < earliest) { pos += earliest - at; when = earliest; } else when = at;
    }
    this.startPos = pos;
    this.startCtxTime = when;
    this.playing = true;
    const loop = this.activeLoop(pos);
    this.scheduleClips(pos, this.startCtxTime, loop ? loop.end : Infinity);
    if (this.metronome) this.startMetronome(pos, this.startCtxTime);
    if (loop) this.armLoop(loop, pos, this.startCtxTime);
    this.onTransport?.();
  }

  /** The loop to apply when playing from `fromPos`: none while recording (the take
   *  is one continuous capture) or when starting past the loop's end. */
  private activeLoop(fromPos: number): { start: number; end: number } | null {
    if (!this.store.project.loopEnabled || this.recording) return null;
    const r = this.store.loopRange();
    if (!r || fromPos >= r.end - 1e-3) return null;
    return r;
  }

  /** Queue the next loop pass at the exact audio-clock time the current one ends.
   *  The current pass is already cut off at `loop.end`, so the seam is gapless. */
  private armLoop(loop: { start: number; end: number }, passPos: number, passCtxTime: number) {
    clearTimeout(this.loopTimer);
    const at = passCtxTime + (loop.end - passPos);
    this.loopTimer = setTimeout(() => {
      if (!this.playing) return;
      // Throttled timer (background tab) missed the seam: restart from the loop start.
      if (this.ctx.currentTime > at - 0.02) { void this.play(loop.start); return; }
      this.nextPass = { at, pos: loop.start };
      this.scheduleClips(loop.start, at, loop.end);
      if (this.metronome) { this.stopMetronome(); this.startMetronome(loop.start, at); }
      this.armLoop(loop, loop.start, at);
    }, Math.max(0, (at - this.ctx.currentTime - 0.3) * 1000));
  }

  /** Re-apply loop settings to a running transport (loop toggled / In-Out edited). */
  refreshLoop() {
    if (this.playing && !this.recording) void this.play(this.position());
  }

  pause() {
    if (!this.playing) return;
    this.pausedPos = this.position();
    this.stopSources();
    this.stopMetronome();
    clearTimeout(this.loopTimer); this.nextPass = null;
    this.playing = false;
    this.onTransport?.();
  }

  stop() {
    this.pausedPos = 0;
    this.stopSources();
    this.stopMetronome();
    clearTimeout(this.loopTimer); this.nextPass = null;
    this.playing = false;
    this.recording = false;
    this.onTransport?.();
  }

  seek(pos: number) {
    const wasPlaying = this.playing;
    if (wasPlaying) this.stopSources();
    this.pausedPos = Math.max(0, pos);
    if (wasPlaying) this.play(this.pausedPos);
    else this.onTransport?.();
  }

  private stopSources() {
    for (const s of this.sources) { try { s.stop(); } catch {} try { s.disconnect(); } catch {} }
    this.sources.clear();
  }

  private reschedTimer: any = null;
  /** Debounced: rebuild the scheduled clip sources from the current position
   *  without moving the playhead (used when clips change during playback). */
  private queueReschedule() {
    clearTimeout(this.reschedTimer);
    this.reschedTimer = setTimeout(() => {
      if (!this.playing) return;
      const lead = 0.03;
      const pos = this.position() + lead;
      this.stopSources();
      clearTimeout(this.loopTimer); this.nextPass = null;
      this.startPos = pos;
      this.startCtxTime = this.ctx.currentTime + lead; // position() stays continuous
      const loop = this.activeLoop(pos);
      this.scheduleClips(pos, this.startCtxTime, loop ? loop.end : Infinity);
      if (loop) this.armLoop(loop, pos, this.startCtxTime);
    }, 40);
  }

  /** Schedule everything that plays from timeline `fromPos` (at audio time `when`)
   *  up to `untilPos` (the loop end when looping, so passes butt together). */
  private scheduleClips(fromPos: number, when: number, untilPos = Infinity) {
    for (const track of this.store.project.tracks) {
      const ch = this.channels.get(track.id);
      if (!ch) continue;
      // Crossfade transitions extend clips into their handles with longer fades.
      const plans = audioPlans(track.clips, (c) => this.store.bufferDuration(c.assetId));
      for (const clip of track.clips) {
        if (clip.takeActive === false || clip.disabled) continue;
        let plan = plans.get(clip.id)!;
        // a loop cuts the clip at its end: shorten it with a 5 ms de-click fade
        if (plan.end > untilPos) plan = { ...plan, end: untilPos, fadeOut: Math.min(0.005, Math.max(0, untilPos - plan.start)) };
        if (plan.end <= fromPos || plan.start >= untilPos) continue;
        const buffer = this.store.buffers.get(clip.assetId);
        if (!buffer) continue;
        const speed = clipSpeed(clip);
        const startInTimeline = Math.max(plan.start, fromPos);
        const bufferOffset = plan.offset + (startInTimeline - plan.start) * speed;
        const playDur = plan.end - startInTimeline;
        if (playDur <= 0) continue;
        const src = this.ctx.createBufferSource();
        src.buffer = buffer;
        if (speed !== 1) src.playbackRate.value = speed;
        const cg = this.ctx.createGain();
        const startAt = when + (startInTimeline - fromPos);
        scheduleFades(cg.gain, clip.gain, plan, startInTimeline, startAt);
        src.connect(cg);
        cg.connect(ch.fxInput);
        src.start(startAt, Math.max(0, bufferOffset), playDur * speed); // duration is in buffer time
        this.sources.add(src);
        src.onended = () => { this.sources.delete(src); try { cg.disconnect(); } catch {} };
      }
    }
  }

  // ---- metronome (lookahead scheduler) --------------------------------
  private startMetronome(fromPos: number, when: number) {
    const spb = 60 / this.store.project.tempo;
    this.metroBeat = Math.ceil(fromPos / spb);
    this.metroNextBeatTime = when + (this.metroBeat * spb - fromPos);
    const tick = () => {
      const ahead = this.ctx.currentTime + 0.2;
      while (this.metroNextBeatTime < ahead) {
        const accent = this.metroBeat % this.store.project.beatsPerBar === 0;
        this.clickAt(this.metroNextBeatTime, accent);
        this.metroBeat++;
        this.metroNextBeatTime += spb;
      }
    };
    tick();
    this.metroTimer = setInterval(tick, 60);
  }
  private stopMetronome() { clearInterval(this.metroTimer); this.metroTimer = null; }
  private clickAt(t: number, accent: boolean) {
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.frequency.value = accent ? 1500 : 900;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(accent ? 0.5 : 0.3, t + 0.001);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    osc.connect(g);
    g.connect(this.masterFader);
    osc.start(t);
    osc.stop(t + 0.06);
  }

  setMetronome(on: boolean) {
    this.metronome = on;
    if (on && this.playing) this.startMetronome(this.position(), this.ctx.currentTime + 0.06);
    if (!on) this.stopMetronome();
  }

  // ---- metering --------------------------------------------------------
  private peakOf(an: AnalyserNode): number {
    an.getFloatTimeDomainData(this.meterBuf);
    let peak = 0;
    for (let i = 0; i < this.meterBuf.length; i++) {
      const v = Math.abs(this.meterBuf[i]);
      if (v > peak) peak = v;
    }
    return peak;
  }
  levels(): { master: number; tracks: Record<string, number> } {
    const tracks: Record<string, number> = {};
    for (const [id, ch] of this.channels) tracks[id] = this.peakOf(ch.analyser);
    return { master: this.peakOf(this.masterAnalyser), tracks };
  }
}
